// =============================================================================
// RequestPeopleModal.jsx
// =============================================================================
// "People on this request" — a read-only modal listing every actor who has
// touched a request through its lifecycle (took it on, assigned, released,
// commented, confirmed decisions, actioned), from the db-61 audit trail via
// get_request_participants. Opened from the Request Summary panel header.
//
// Props:
//   requestId — numeric request id (get_request_participants arg)
//   refNo     — display ref (e.g. "2026-00124")
//   onClose   — fn() to dismiss
// =============================================================================

import React from "react";
import { getRequestParticipants } from "../../data/auditLogs";
import { listRolePermsByIds } from "../../data/profiles";
import { fmtDateTimeLocal, formatRef } from "../../utils/helpers";
import { ModalBackdrop } from "../form/FormUI.jsx";

// Humanise the raw audit action strings into what the person did.
const ACTION_LABELS = {
  task_taken_on: "Took on",
  task_assigned: "Assigned",
  task_released: "Released",
  task_actioned: "Actioned",
  approvals_confirmed: "Confirmed decisions",
  comment_sent: "Commented",
};

// Friendly role labels (DB role value → what staff call it). The admin tier is
// resolved by roleLabel() below, not here (it splits on a capability).
const ROLE_LABELS = {
  superuser: "SuperUser",
  admin: "Admin",
  staff: "Responder",
  combined: "Combined",
  picker: "Picker",
  shelter: "DFV Service",
};

// The admin tier splits by capability, matching the Resources → Staff List (John,
// 6 Sep 2026): an admin who holds the "monitored_approval" capability ("Approve
// Monitored DFV Services") reads as "Supervisor"; every other admin reads as
// "Admin". `permsById` maps a participant's user_id → their admin_permissions.
function roleLabel(p, permsById) {
  if (p.role === "admin") {
    const perms = permsById.get(p.user_id) || [];
    return perms.includes("monitored_approval") ? "Supervisor" : "Admin";
  }
  return ROLE_LABELS[p.role] || p.role || "—";
}

function actionSummary(actions) {
  const list = (actions || []).map(function (a) {
    return ACTION_LABELS[a] || a;
  });
  return list.length > 0 ? list.join(", ") : "—";
}

export function RequestPeopleModal({ requestId, refNo, onClose }) {
  const [loading, setLoading] = React.useState(true);
  const [people, setPeople] = React.useState([]);
  // user_id → admin_permissions[], so an admin participant can be shown as
  // "Supervisor" only when they hold the monitored_approval capability.
  const [permsById, setPermsById] = React.useState(new Map());

  React.useEffect(
    function () {
      let live = true;
      getRequestParticipants(requestId).then(function (rows) {
        if (!live) return;
        const list = rows || [];
        // Only admins need a capability lookup — every other role labels directly.
        const adminIds = list
          .filter(function (p) {
            return p.role === "admin";
          })
          .map(function (p) {
            return p.user_id;
          });
        if (adminIds.length === 0) {
          setPeople(list);
          setPermsById(new Map());
          setLoading(false);
          return;
        }
        listRolePermsByIds(adminIds).then(function (profs) {
          if (!live) return;
          const m = new Map();
          (profs || []).forEach(function (pr) {
            m.set(pr.id, Array.isArray(pr.admin_permissions) ? pr.admin_permissions : []);
          });
          setPeople(list);
          setPermsById(m);
          setLoading(false);
        });
      });
      return function () {
        live = false;
      };
    },
    [requestId]
  );

  function fmt(iso) {
    if (!iso) return "—";
    return fmtDateTimeLocal(iso) || "—";
  }

  return (
    <ModalBackdrop
      onClose={onClose}
      portal
      contentStyle={{
        background: "white",
        borderRadius: 12,
        padding: 0,
        width: 640,
        maxWidth: "92vw",
        maxHeight: "82vh",
        overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "#37474F",
          color: "white",
          padding: "13px 18px",
          fontWeight: 700,
          fontSize: 15,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>People on this request — {formatRef(refNo)}</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            background: "transparent",
            border: "none",
            color: "white",
            fontSize: 20,
            lineHeight: 1,
            cursor: "pointer",
            padding: 0,
          }}
        >
          ×
        </button>
      </div>

      {/* Body */}
      <div style={{ padding: "8px 0", overflowY: "auto", flex: 1 }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: "center", color: "#607D79" }}>Loading…</div>
        ) : people.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: "#607D79" }}>
            No recorded activity on this request yet.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#FAFAFA" }}>
                {["Person", "Role", "What they did", "First", "Last"].map(function (h) {
                  return (
                    <th
                      key={h}
                      style={{
                        textAlign: "left",
                        padding: "8px 14px",
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "#607D79",
                        borderBottom: "2px solid #E0E0E0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {people.map(function (p) {
                return (
                  <tr key={p.user_id}>
                    <td
                      style={{
                        padding: "9px 14px",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#1A2E2B",
                        borderBottom: "1px solid #F0F0F0",
                      }}
                    >
                      {p.display_name || p.user_id}
                    </td>
                    <td
                      style={{
                        padding: "9px 14px",
                        fontSize: 12,
                        color: "#607D79",
                        borderBottom: "1px solid #F0F0F0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {roleLabel(p, permsById)}
                    </td>
                    <td
                      style={{
                        padding: "9px 14px",
                        fontSize: 13,
                        color: "#37474F",
                        borderBottom: "1px solid #F0F0F0",
                      }}
                    >
                      {actionSummary(p.actions)}
                    </td>
                    <td
                      style={{
                        padding: "9px 14px",
                        fontSize: 12,
                        color: "#607D79",
                        borderBottom: "1px solid #F0F0F0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmt(p.first_touch)}
                    </td>
                    <td
                      style={{
                        padding: "9px 14px",
                        fontSize: 12,
                        color: "#607D79",
                        borderBottom: "1px solid #F0F0F0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmt(p.last_touch)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </ModalBackdrop>
  );
}
